// Account page (every role). Edit your own profile (name, email, phone, location). Clients see and
// edit their company's shipping address; a rep edits the client currently selected in the top bar.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import ShippingAddressCard from '../components/ShippingAddressCard.jsx';
import { updateProfile } from '../api/me.js';
import { useAuthStore } from '../stores/authStore.js';

const ROLE_LABEL = { INTERNAL_STAFF: 'Internal staff', REP: 'Wholesale rep', CLIENT: 'Wholesale client' };
const field = 'mt-1 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

function ProfileCard({ user, setUser }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: user?.location || '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  // Google-authenticated staff can't change their email — it's their Google identity.
  const emailLocked = user?.authProvider === 'GOOGLE';

  const dirty =
    form.name !== (user?.name || '') ||
    (!emailLocked && form.email !== (user?.email || '')) ||
    form.phone !== (user?.phone || '') ||
    form.location !== (user?.location || '');

  const save = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const updated = await updateProfile({
        name: form.name.trim(),
        ...(emailLocked ? {} : { email: form.email.trim() }),
        phone: form.phone.trim(),
        location: form.location.trim(),
      });
      setUser(updated); // keep the top bar + session in sync
      setMsg('Profile updated.');
    } catch (e) {
      setError(e.response?.data?.error?.message || 'Could not save your profile.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 max-w-xl rounded-lg border border-line bg-surface p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Profile</h2>
        <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-text-muted">{ROLE_LABEL[user?.role] || user?.role}</span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium text-text-muted">Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-text-muted">Email (your login)</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={emailLocked}
            title={emailLocked ? 'Managed by Google sign-in' : undefined}
            className={`${field} ${emailLocked ? 'cursor-not-allowed bg-surface-raised text-text-muted' : ''}`}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-text-muted">Phone</span>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. +61 400 000 000" className={field} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-text-muted">Location</span>
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City / region" className={field} />
        </label>
      </div>
      <p className="mt-2 text-[11px] text-text-muted">
        {emailLocked
          ? 'Your email is managed by Google sign-in and can’t be changed here.'
          : 'Changing your email changes the address you sign in with and where we send login codes.'}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={save}
          disabled={busy || !dirty}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        {msg && <span className="text-sm text-success">{msg}</span>}
        {error && <span className="text-sm text-error">{error}</span>}
      </div>
    </div>
  );
}

export default function Account() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const role = user?.role;

  return (
    <>
      <PageHeader title="Account" subtitle="Your profile and contact details." />

      <ProfileCard user={user} setUser={setUser} />

      {(role === 'REP' || role === 'INTERNAL_STAFF') && (
        <div className="mb-4 max-w-xl">
          <Link to={`/team/${user.id}`} className="text-sm font-medium text-accent hover:underline">
            View my performance dashboard →
          </Link>
        </div>
      )}

      {role === 'CLIENT' && <ShippingAddressCard />}
    </>
  );
}
